import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Skeleton, Tab, Tabs, Tooltip } from "@mui/material";
import React, { useContext, useEffect, useMemo, useState } from "react";
import BrandAuditIndexBreakups from "../../Component/BrandIndexHistory/BrandAuditIndexBreakups";
import BrandPillarsTable from "../../Component/BrandIndexHistory/BrandPillarsTable";
import ProductDimensionsTable from "../../Component/BrandIndexHistory/ProductDimensionsTable";
import PurchaseCriteriaTable from "../../Component/BrandIndexHistory/PurchaseCriteriaTable";
import Modal from "../../Component/Modal/Modal";
import BrandAvatar from "../../Component/Avatar/Avatar";
import { OVERVIEW_LLM_LABELS } from "../../utils/constant";
import { ENTITY_LABELS } from "../../utils/labelMapping";
import { CategoryAnalysisContext } from "./category-analysis-context";
import classes from "./CategoryScoreCards.module.css";
import {
  getBrandPillarsAvg,
  getProductDimensionsAvg,
  getPurchaseCriteriaAvg,
  calcBrandIndexScore,
  RBI_COLORS,
  RBI_WEIGHTS,
} from "../../utils/brandIndexCalc";
import RBIMetricCardsView from "./RBIMetricCardsView";
import { ALL_METRIC_VALUES } from "./metricOptions";

// ─── RBI Row View Toggle ───────────────────────────────────────────────────────
// "stacked" → contribution-based stacked bar
// "cards"   → metric score cards (Visibility / Sentiment / Brand Pillars / Purchase + RBI total)
const RBI_ROW_VIEW = "cards"

// The RBI composite table/score is only meaningful when all of its underlying metrics were run
const RBI_REQUIRED_METRICS = ["rank", "sentiment", "coreAttribute", "categoryDimension"];

// ─── Palette & Constants ─────────────────────────────────────────────────────

const COMPARE_PALETTE = [
  "#6b95e8", "#ee8282", "#a87fd4", "#3ebfa2",
  "#f5a050", "#5ab0d4", "#e07ab0", "#6ecce8",
];

const BRAND_PALETTE = [
  "#6b95e8", "#ee8282", "#52c28a", "#f5c040",
  "#b07fd4", "#3ebfa2", "#f07098", "#5ab8e8",
  "#f5a050", "#88c840", "#8888e0", "#3ec8b8",
];

const MAX_SCORE = { total: 100, rank: 50, favorability: 50, brandPillars: 10, purchaseCriteria: 10 };
const COMPARE_ALL_VALUE = "__compare_all__";


const METRIC_META = {
  total:           { subtitle: "Brand Relevance Index",    desc: "Combined visibility & favorability" },
  rank:            { subtitle: "Visibility Score",          desc: "AI response mention frequency" },
  favorability:    { subtitle: "Favorability Score",        desc: "Positive brand perception strength" },
  brandPillars:    { subtitle: "Brand Pillars / Product Dimensions", desc: "Average attribute score (0–10)" },
  purchaseCriteria:{ subtitle: "Purchase Criteria",         desc: "Average purchase criteria rating (0–10)" },
};

// ─── Inline SVG icons ────────────────────────────────────────────────────────

const LLM_ICON = (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="6" y="6" width="12" height="12" rx="2"/>
    <path d="M9 2v4M15 2v4M9 18v4M15 18v4M2 9h4M2 15h4M18 9h4M18 15h4"/>
  </svg>
);

const MARKET_ICON = (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="9"/>
    <path d="M3.6 9h16.8M3.6 15h16.8M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18"/>
  </svg>
);

const PERSONA_ICON = (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
    <circle cx="12" cy="7" r="4"/>
  </svg>
);

// ─── Helpers ─────────────────────────────────────────────────────────────────

function extractDomain(input) {
  if (!input) return null;
  if (/^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(input)) return input;
  try {
    if (!/^https?:\/\//i.test(input)) input = "http://" + input;
    const urlObj = new URL(input);
    let hostname = urlObj.hostname;
    const parts = hostname.split(".");
    if (parts.length > 2) hostname = parts.slice(-2).join(".");
    return hostname;
  } catch { return null; }
}

function getLabel(value) {
  return OVERVIEW_LLM_LABELS[value] || value || "";
}

// LLM key → provider domain for logo.dev
const LLM_DOMAIN_MAP = {
  "gpt-4o": "openai.com", "gpt-4o-web-search": "openai.com",
  "gpt-4o-agent": "openai.com", "gpt-4o-agent-web-search": "openai.com",
  "gpt-5": "openai.com", "gpt-5-search-enabled": "openai.com",
  "gpt-5.5": "openai.com", "gpt-5.5-search-enabled": "openai.com",
  "gpt-5.4": "openai.com", "gpt-5.4-search-enabled": "openai.com",
  "gpt-5.2": "openai.com", "gpt-5.2-search-enabled": "openai.com",
  "gpt-5.5": "openai.com", "gpt-5.5-search-enabled": "openai.com",
  "gpt-5.1": "openai.com", "gpt-5.1-search-enabled": "openai.com",
  "gpt-5.1-chat": "openai.com", "gpt-5.1-chat-search-enabled": "openai.com",
  "gpt-4.1-nano": "openai.com", "gpt-4o-mini": "openai.com",
  "gpt-5.4-nano" : "openai.com",
  "gpt-5.6": "openai.com", "gpt-5.6-search-enabled": "openai.com",
  "gpt-6": "openai.com", "gpt-6-search-enabled": "openai.com",
  "gpt-6.1": "openai.com", "gpt-6.1-search-enabled": "openai.com",
  "gpt-6-luna": "openai.com",
  "chatgpt_bd": "openai.com",
  "microsoft_copilot_bd":"copilot.microsoft.com",
  "gemini_bd" : "gemini.google.com",
  "Claude3": "anthropic.com", "Claude3Search": "anthropic.com",
  "Claude4": "anthropic.com", "Claude4Search": "anthropic.com",
  "Claude-4.5": "anthropic.com", "Claude-4.5-Search": "anthropic.com",
  "Claude-4.6": "anthropic.com", "Claude-4.6-Search": "anthropic.com",
  "Claude-5": "anthropic.com", "Claude-5-Search": "anthropic.com",
  "Claude-5.5": "anthropic.com", "Claude-5.5-Search": "anthropic.com",
  "Claude-5.5-Haiku": "anthropic.com", "Claude-5.5-Sonnet-Search": "anthropic.com",
  "Claude4-5": "anthropic.com",
  "Gemini": "gemini.google.com", "GeminiV25": "gemini.google.com",
  "GeminiV25Search": "gemini.google.com",
  "Gemini-3.0": "gemini.google.com", "Gemini-3.0-Search": "gemini.google.com",
  "Gemini-3.1": "gemini.google.com", "Gemini-3.1-Search": "gemini.google.com",
  "Gemini-3.8-Flash": "gemini.google.com", "Gemini-3.8-Flash-Search": "gemini.google.com",
  "llama3": "meta.com",
  "Perplexity": "perplexity.ai", "Perplexity-search-enabled": "perplexity.ai",
  "deepseek_together": "deepseek.com",
  "grok-4-1": "x.ai", "grok-4-1-web-search": "x.ai",
  "grok-4.3": "x.ai", "grok-4.3-web-search": "x.ai",
  "grok-4.5": "x.ai", "grok-4.5-web-search": "x.ai",
  "grok-4.7": "x.ai", "grok-4.7-search-enabled": "x.ai",
};

function getLLMLogoUrl(llmKey) {
  if (!llmKey) return null;
  if (llmKey.toLowerCase().startsWith("claude")) return "/claude-logo.png";
  const domain = LLM_DOMAIN_MAP[llmKey] || (llmKey.toLowerCase().startsWith("gpt") ? "openai.com" : null);
  return domain ? `https://img.logo.dev/${domain}?token=pk_SaDf4t2eRsW5Fc84zgJRZA` : null;
}

function getBrandDataAt(brand_scores, llm, category, market, persona, idx) {
  const arr = brand_scores?.[llm]?.[category]?.[market]?.[persona];
  return Array.isArray(arr) ? (arr[idx] ?? null) : null;
}

function computeAvg(segs) {
  const valid = (segs || []).filter(s => s.score !== null && s.score !== undefined);
  return valid.length > 0 ? valid.reduce((sum, s) => sum + s.score, 0) / valid.length : -1;
}

const TOOLTIP_SX = {
  bgcolor: "#1e2340", fontSize: "0.73rem", borderRadius: "10px",
  padding: "9px 12px", boxShadow: "0 6px 20px rgba(0,0,0,0.28)",
  pointerEvents: "all", "& .MuiTooltip-arrow": { color: "#1e2340" },
};

const ICON_MIN_PX = 8;

const SCORE_TYPE_COLOR = {
  total:            "#6b95e8",
  rank:             RBI_COLORS.visibility,
  favorability:     RBI_COLORS.sentiment,
  brandPillars:     RBI_COLORS.brandPillars,
  purchaseCriteria: RBI_COLORS.purchaseCriteria,
};

// ─── Segmented Bar — Single View ──────────────────────────────────────────────

function SegmentedBrandBar({ segments, maxScore, onSegmentClick, showDecimal = false }) {
  const validScores = segments.filter(s => s.score !== null && s.score !== undefined);
  const rawAvg = validScores.length ? validScores.reduce((s, x) => s + x.score, 0) / validScores.length : null;
  const n = segments.length;
  const [openIdx, setOpenIdx] = useState(-1);
  const slotPct = 100 / n;

  return (
    <div className={classes.segRow}>
      <div className={classes.unifiedTrack}>
        {segments.map((seg, i) => {
          const scoreKnown = seg.score !== null && seg.score !== undefined;
          const hasScore = scoreKnown && seg.score > 0;
          const fillPct = hasScore ? (seg.score / maxScore) * 100 : 0;
          return (
            <Tooltip key={i} open={openIdx === i} onOpen={() => setOpenIdx(i)}
              onClose={() => setOpenIdx(cur => cur === i ? -1 : cur)}
              arrow placement="top" enterDelay={100}
              componentsProps={{ tooltip: { sx: TOOLTIP_SX } }}
              title={
                <div style={{ minWidth: 155 }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <div style={{ width: 8, height: 8, borderRadius: "50%", background: seg.color, flexShrink: 0 }} />
                      <span style={{ fontWeight: 600, fontSize: "0.76rem" }}>{seg.entityLabel || seg.entity}</span>
                    </div>
                    <div onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); setOpenIdx(-1); onSegmentClick && onSegmentClick(seg); }}
                      style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 3, padding: "2px 7px 2px 5px", borderRadius: 5, background: "rgba(255,255,255,0.13)", color: "rgba(255,255,255,0.90)", flexShrink: 0, userSelect: "none" }}>
                      <InfoOutlinedIcon style={{ fontSize: 12 }} />
                      <span style={{ fontSize: "0.65rem", fontWeight: 600 }}>Details</span>
                    </div>
                  </div>
                  <div style={{ fontSize: "0.71rem", opacity: 0.72, marginTop: 5, paddingLeft: 14 }}>
                    {scoreKnown ? <>Score: <strong style={{ opacity: 1 }}>{seg.score}</strong></> : <em>No data</em>}
                  </div>
                </div>
              }
            >
              {/* Slot spans the full 1/n width — tooltip fires on fill AND empty space */}
              <div className={classes.segSlot} style={{ width: `${slotPct}%` }}>
                {hasScore && (
                  <div className={classes.segStrip}
                    style={{ width: `${fillPct}%`, background: seg.color, opacity: 0.65 }}
                  />
                )}
              </div>
            </Tooltip>
          );
        })}
      </div>
      <span className={classes.scoreValue}>
        {rawAvg !== null
          ? (showDecimal ? Number(rawAvg).toFixed(1) : Math.round(rawAvg))
          : <span className={classes.scoreNA}>—</span>}
      </span>
    </div>
  );
}

// ─── Individual Segment Row (expanded compare view) ───────────────────────────

function IndividualSegRow({ seg, maxScore, onSegmentClick, compareMode }) {
  const [tipOpen, setTipOpen] = useState(false);
  const scoreKnown = seg.score !== null && seg.score !== undefined;
  const pct = scoreKnown && seg.score > 0 ? Math.min((seg.score / maxScore) * 100, 100) : 0;
  const logoUrl = compareMode === "llm" ? getLLMLogoUrl(seg.entity) : null;
  const label = seg.entityLabel || seg.entity || "";
  const entityCell = compareMode === "llm" ? (
    logoUrl
      ? <img src={logoUrl} alt={label} className={classes.indEntityLogo} onError={e => { e.target.style.display = "none"; e.target.nextSibling.style.display = "flex"; }} />
      : <span className={classes.indEntityInitial} style={{ background: seg.color }}>{label.charAt(0).toUpperCase()}</span>
  ) : (
    <>
      <span className={classes.indDot} style={{ background: seg.color }} />
      <span className={classes.indEntityName}>{label}</span>
    </>
  );

  return (
    <Tooltip open={tipOpen} onOpen={() => setTipOpen(true)} onClose={() => setTipOpen(false)}
      arrow placement="top" enterDelay={100} componentsProps={{ tooltip: { sx: TOOLTIP_SX } }}
      title={
        <div style={{ minWidth: 155 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <div style={{ width: 8, height: 8, borderRadius: "50%", background: seg.color, flexShrink: 0 }} />
              <span style={{ fontWeight: 600, fontSize: "0.76rem" }}>{label}</span>
            </div>
            <div onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); setTipOpen(false); onSegmentClick && onSegmentClick(); }}
              style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 3, padding: "2px 7px 2px 5px", borderRadius: 5, background: "rgba(255,255,255,0.13)", color: "rgba(255,255,255,0.90)", flexShrink: 0, userSelect: "none" }}>
              <InfoOutlinedIcon style={{ fontSize: 12 }} />
              <span style={{ fontSize: "0.65rem", fontWeight: 600 }}>Details</span>
            </div>
          </div>
          <div style={{ fontSize: "0.71rem", opacity: 0.72, marginTop: 5, paddingLeft: 14 }}>
            {scoreKnown ? <>Score: <strong style={{ opacity: 1 }}>{seg.score}</strong></> : <em>No data</em>}
          </div>
        </div>
      }
    >
      <div className={classes.indExpandRow}>
        <div className={`${classes.indEntityCell} ${compareMode === "llm" ? classes.indEntityCellLLM : ""}`}>
          {entityCell}
          {compareMode === "llm" && logoUrl && (
            <span className={classes.indEntityInitial} style={{ background: seg.color, display: "none" }}>
              {label.charAt(0).toUpperCase()}
            </span>
          )}
        </div>
        <div className={classes.indTrackArea}>
          <div className={classes.indTrack}>
            <div className={classes.indFill} style={{ width: `${pct}%`, background: seg.color, opacity: 0.65 }} />
          </div>
          <span className={classes.indVal}>{scoreKnown ? seg.score : "—"}</span>
        </div>
      </div>
    </Tooltip>
  );
}

// ─── Compare Mode Brand Row ───────────────────────────────────────────────────

function BrandRowCompare({ brand, segments, maxScore, onAggClick, onSegmentClick, compareMode, color, showDecimal = false }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [barTooltipOpen, setBarTooltipOpen] = useState(false);

  const validScores = (segments || []).filter(s => s.score !== null && s.score !== undefined);
  const rawAvg = validScores.length ? validScores.reduce((s, x) => s + x.score, 0) / validScores.length : null;
  const avgScore = rawAvg !== null ? rawAvg : null;
  const displayScore = avgScore !== null ? (showDecimal ? Number(avgScore).toFixed(1) : Math.round(avgScore)) : null;
  const aggPct = avgScore !== null ? Math.min((avgScore / maxScore) * 100, 100) : 0;

  return (
    <div className={`${classes.brandRowCompare} ${isExpanded ? classes.brandRowCompareOpen : ""}`}>
      <div className={classes.rowLeftIndicator} />
      <div className={classes.compareAggRow} onClick={() => setIsExpanded(prev => !prev)}>
        <div className={classes.brandAvatarCell}>
          <BrandAvatar domainName={brand.domain || "example.com"} brandName={brand.name} />
        </div>
        <Tooltip open={barTooltipOpen} onOpen={() => setBarTooltipOpen(true)} onClose={() => setBarTooltipOpen(false)}
          arrow placement="top" enterDelay={200} componentsProps={{ tooltip: { sx: TOOLTIP_SX } }}
          title={
            <div style={{ minWidth: 168 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                <span style={{ fontWeight: 600, fontSize: "0.76rem" }}>Avg Score: <strong>{displayScore ?? "—"}</strong></span>
                <div onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); setBarTooltipOpen(false); onAggClick && onAggClick(); }}
                  style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 3, padding: "2px 7px 2px 5px", borderRadius: 5, background: "rgba(255,255,255,0.13)", color: "rgba(255,255,255,0.90)", flexShrink: 0, userSelect: "none" }}>
                  <InfoOutlinedIcon style={{ fontSize: 12 }} />
                  <span style={{ fontSize: "0.65rem", fontWeight: 600 }}>Details</span>
                </div>
              </div>
              <div style={{ fontSize: "0.71rem", opacity: 0.72, marginTop: 4 }}>
                Average across {validScores.length} comparison{validScores.length !== 1 ? "s" : ""}
              </div>
            </div>
          }
        >
          <div className={classes.segRow} onClick={(e) => e.stopPropagation()}>
            <div className={classes.unifiedTrack}>
              <div className={classes.aggFill} style={{ width: `${aggPct}%`, background: color || undefined, opacity: 0.65 }} />
            </div>
            <span className={classes.scoreValue}>
              {displayScore !== null ? displayScore : <span className={classes.scoreNA}>—</span>}
            </span>
          </div>
        </Tooltip>
        <span className={`${classes.expandCaret} ${isExpanded ? classes.expandCaretOpen : ""}`}>
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
            <path d="M2 3.5l3 3 3-3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
      </div>
      <div style={{ maxHeight: isExpanded ? "400px" : "0", overflow: "hidden", transition: "max-height 0.28s cubic-bezier(0.4,0,0.2,1)" }}>
        {segments && segments.length > 0 && (
          <div className={classes.indExpandInner}>
            {segments.map((seg, i) => (
              <IndividualSegRow key={i} seg={seg} maxScore={maxScore} compareMode={compareMode} onSegmentClick={() => onSegmentClick && onSegmentClick(seg)} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Brand Row ────────────────────────────────────────────────────────────────

function BrandRow({ brand, segments, maxScore, onInfoClick, onSegmentClick, isCompareMode, compareMode, color, showDecimal = false }) {
  if (isCompareMode) {
    return <BrandRowCompare brand={brand} segments={segments} maxScore={maxScore} compareMode={compareMode} color={color} onAggClick={onInfoClick} onSegmentClick={onSegmentClick} showDecimal={showDecimal} />;
  }
  return (
    <div className={classes.brandRow}>
      <div className={classes.brandAvatarCell}>
        <BrandAvatar domainName={brand.domain || "example.com"} brandName={brand.name} />
      </div>
      {segments ? (
        <SegmentedBrandBar segments={segments} maxScore={maxScore} onSegmentClick={onSegmentClick} showDecimal={showDecimal} />
      ) : (
        <div className={classes.segRow}>
          <div className={classes.unifiedTrack}>
            <Skeleton variant="rectangular" width="100%" height="100%" style={{ borderRadius: 11 }} />
          </div>
          <span className={classes.scoreValue}>—</span>
        </div>
      )}
    </div>
  );
}

// ─── RBI Stacked Bar (single default mode row) ───────────────────────────────

const RBI_SEGMENT_DEFS = [
  { key: "visibility",        label: "Visibility",     weight: RBI_WEIGHTS.visibility },
  { key: "sentiment",         label: "Sentiment",      weight: RBI_WEIGHTS.sentiment },
  { key: "brandPillars",      label: "Brand Pillars",  weight: RBI_WEIGHTS.brandPillars },
  { key: "purchaseCriteria",  label: "Purchase Criteria", weight: RBI_WEIGHTS.purchaseCriteria },
];

function RBIStackedSingleRow({ brand, entry, onInfoClick }) {
  const { rank, favorability, brandPillarsAvg, purchaseCriteriaAvg,
          contributions, total, visNorm, sentNorm, pillarsNorm, purchaseNorm, isProduct } = entry || {};

  const SEGS = [
    { key: "visibility",       label: ENTITY_LABELS.rank,                                 color: RBI_COLORS.visibility,       raw: rank,               norm: visNorm    ?? 0, weight: RBI_WEIGHTS.visibility,       contribution: contributions?.visibility       ?? 0 },
    { key: "sentiment",        label: "Sentiment",                                        color: RBI_COLORS.sentiment,        raw: favorability,       norm: sentNorm   ?? 0, weight: RBI_WEIGHTS.sentiment,        contribution: contributions?.sentiment        ?? 0 },
    { key: "brandPillars",     label: isProduct ? ENTITY_LABELS.productDimension : ENTITY_LABELS.brandPillar, color: RBI_COLORS.brandPillars,     raw: brandPillarsAvg,    norm: pillarsNorm ?? 0, weight: RBI_WEIGHTS.brandPillars,    contribution: contributions?.brandPillars     ?? 0 },
    { key: "purchaseCriteria", label: ENTITY_LABELS.purchaseCriteria,                     color: RBI_COLORS.purchaseCriteria, raw: purchaseCriteriaAvg, norm: purchaseNorm ?? 0, weight: RBI_WEIGHTS.purchaseCriteria, contribution: contributions?.purchaseCriteria ?? 0 },
  ];

  // Each segment width = actual contribution out of 100 max RBI,
  // with a 2% floor so zero-contribution metrics still show as a thin sliver.
  const MIN_SEG_PCT = 2;
  const rawWidths = SEGS.map(seg => Math.max(MIN_SEG_PCT, (seg.contribution / 100) * 100));
  // Scale down so total never exceeds 100%
  const rawTotal = rawWidths.reduce((s, w) => s + w, 0);
  const scale = rawTotal > 100 ? 100 / rawTotal : 1;
  const segWidths = rawWidths.map(w => w * scale);

  return (
    <div className={classes.rbiStackedRow}>
      <div className={classes.brandAvatarCell}>
        <BrandAvatar domainName={brand.domain || "example.com"} brandName={brand.name} />
      </div>
      <div className={classes.segRow}>
        {/* Bar: width of each segment = actual contribution (2% min); remainder is gray empty space */}
        <div className={classes.rbiStackedTrack}>
          {SEGS.map((seg, segIdx) => {
            const widthPct = segWidths[segIdx];
            const isLast   = segIdx === SEGS.length - 1;
            return (
              <Tooltip key={seg.key} arrow placement="top"
                componentsProps={{ tooltip: { sx: TOOLTIP_SX } }}
                title={
                  <div style={{ minWidth: 185 }}>
                    <div style={{ fontWeight: 600, fontSize: "0.76rem", marginBottom: 6, display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{ width: 8, height: 8, borderRadius: "50%", background: seg.color, display: "inline-block", flexShrink: 0 }} />
                      {seg.label}
                    </div>
                    <div style={{ fontSize: "0.7rem", display: "flex", flexDirection: "column", gap: 3 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                        <span style={{ opacity: 0.75 }}>Raw Score</span>
                        <strong>{seg.raw != null ? Number(seg.raw).toFixed(1) : "—"}</strong>
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                        <span style={{ opacity: 0.75 }}>Normalized</span>
                        <strong>{seg.norm != null ? Number(seg.norm).toFixed(1) : "—"}</strong>
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                        <span style={{ opacity: 0.75 }}>Weight</span>
                        <strong>{Math.round(seg.weight * 100)}%</strong>
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, borderTop: "1px solid rgba(255,255,255,0.15)", marginTop: 3, paddingTop: 3 }}>
                        <span style={{ opacity: 0.75 }}>Contribution</span>
                        <strong style={{ color: seg.color }}>{Number(seg.contribution).toFixed(1)}</strong>
                      </div>
                    </div>
                  </div>
                }
              >
                <div style={{
                  width: `${widthPct}%`,
                  height: "100%",
                  background: seg.color,
                  opacity: seg.contribution > 0 ? 0.65 : 0.2,
                  transition: "width 0.3s ease",
                  borderRight: isLast ? "none" : "2px solid rgba(255,255,255,0.9)",
                  flexShrink: 0,
                }} />
              </Tooltip>
            );
          })}
        </div>
        <span className={classes.scoreValue}>
          {total != null ? Math.round(total) : <span className={classes.scoreNA}>—</span>}
        </span>
      </div>
      <button className={classes.rowInfoBtn} onClick={() => onInfoClick(brand)}>
        <InfoOutlinedIcon style={{ fontSize: 12 }} />
      </button>
    </div>
  );
}

function avgEntry(entries) {
  const valid = (entries || []).filter(e => e.total != null);
  if (!valid.length) return null;
  const avg = (fn) => valid.reduce((s, e) => s + (fn(e) ?? 0), 0) / valid.length;
  return {
    ...valid[0],
    total:            avg(e => e.total),
    visNorm:          avg(e => e.visNorm),
    sentNorm:         avg(e => e.sentNorm),
    pillarsNorm:      avg(e => e.pillarsNorm),
    purchaseNorm:     avg(e => e.purchaseNorm),
    rank:             avg(e => e.rank),
    favorability:     avg(e => e.favorability),
    brandPillarsAvg:  avg(e => e.brandPillarsAvg),
    purchaseCriteriaAvg: avg(e => e.purchaseCriteriaAvg),
    contributions: {
      visibility:       avg(e => e.contributions?.visibility),
      sentiment:        avg(e => e.contributions?.sentiment),
      brandPillars:     avg(e => e.contributions?.brandPillars),
      purchaseCriteria: avg(e => e.contributions?.purchaseCriteria),
    },
  };
}

// ─── RBI Full-Width Score Card ────────────────────────────────────────────────

function RBIStackedScoreCard({ brands, rbiContributionMap, onInfoClick, onSegmentClick, isCompareMode, compareMode }) {
  const [visibleCount, setVisibleCount] = useState(5);
  useEffect(() => { setVisibleCount(5); }, [isCompareMode, brands.length]);

  const sorted = useMemo(() => [...brands].sort((a, b) => {
    const avgA = (() => {
      const entries = rbiContributionMap[a.name] || [];
      const valid = entries.filter(e => e.total != null);
      return valid.length ? valid.reduce((s, e) => s + e.total, 0) / valid.length : -1;
    })();
    const avgB = (() => {
      const entries = rbiContributionMap[b.name] || [];
      const valid = entries.filter(e => e.total != null);
      return valid.length ? valid.reduce((s, e) => s + e.total, 0) / valid.length : -1;
    })();
    return avgB - avgA;
  }), [brands, rbiContributionMap]);

  const visibleBrands = sorted.slice(0, visibleCount);
  const hasMore = sorted.length > visibleCount;

  const legendItems = [
    { key: "visibility",       label: ENTITY_LABELS.rank,             color: RBI_COLORS.visibility,        weight: Math.round(RBI_WEIGHTS.visibility * 100) },
    { key: "sentiment",        label: "Sentiment",         color: RBI_COLORS.sentiment,         weight: Math.round(RBI_WEIGHTS.sentiment * 100) },
    { key: "brandPillars",     label: ENTITY_LABELS.brandPillar,     color: RBI_COLORS.brandPillars,      weight: Math.round(RBI_WEIGHTS.brandPillars * 100) },
    { key: "purchaseCriteria", label: ENTITY_LABELS.purchaseCriteria, color: RBI_COLORS.purchaseCriteria,  weight: Math.round(RBI_WEIGHTS.purchaseCriteria * 100) },
  ];

  return (
    <div className={classes.scoreCard}>
      <div className={classes.cardHeader}>
        <div className={classes.cardTitleGroup}>
          <span className={classes.cardTitle}>RBI</span>
          <span className={classes.cardSubtitle}>Brand Relevance Index</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          {/* Segment legend */}
          {legendItems.map(l => (
            <span key={l.key} style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: "0.68rem", color: "#4b5a8a" }}>
              <span style={{ width: 9, height: 9, borderRadius: 3, background: l.color, display: "inline-block" }} />
              {l.label} <span style={{ opacity: 0.6 }}>({l.weight}%)</span>
            </span>
          ))}
          {isCompareMode && <span className={classes.avgBadge}>Avg</span>}
          <Tooltip arrow placement="top"
            title={<span style={{ fontSize: "0.7rem" }}>Score range: 0–100</span>}
            componentsProps={{ tooltip: { sx: { ...TOOLTIP_SX, padding: "6px 10px" } } }}
          >
            <span className={classes.maxScorePill}>/100</span>
          </Tooltip>
        </div>
      </div>

      {sorted.length === 0 ? (
        <div className={classes.emptyState}>No data</div>
      ) : (
        <>
          {visibleBrands.map(brand => {
            const entries = rbiContributionMap[brand.name] || [];
            const entry = isCompareMode ? avgEntry(entries) : entries[0];
            if (!entry) {
              return (
                <div key={brand.name} className={classes.rbiStackedRow}>
                  <div className={classes.brandAvatarCell}><BrandAvatar domainName={brand.domain || "example.com"} brandName={brand.name} /></div>
                  <div className={classes.segRow}>
                    <div className={classes.rbiStackedTrack}><Skeleton variant="rectangular" width="100%" height="100%" style={{ borderRadius: 9 }} /></div>
                    <span className={classes.scoreValue}>—</span>
                  </div>
                </div>
              );
            }
            return <RBIStackedSingleRow key={brand.name} brand={brand} entry={entry} onInfoClick={onInfoClick} />;
          })}
          {hasMore && (
            <button className={classes.loadMoreBtn} onClick={() => setVisibleCount(c => c + 5)}>
              Load more ({sorted.length - visibleCount} remaining)
            </button>
          )}
        </>
      )}
    </div>
  );
}

// ─── Score Card ───────────────────────────────────────────────────────────────

function ScoreCard({ title, scoreType, maxScoreLabel, brands, segmentsMap, onInfoClick, onSegmentClick, isCompareMode, compareMode }) {
  const maxScore = MAX_SCORE[scoreType] || 50;
  const showDecimal = scoreType === "brandPillars" || scoreType === "purchaseCriteria";
  const meta = METRIC_META[scoreType] || {};
  const metricColor = SCORE_TYPE_COLOR[scoreType];
  const [visibleCount, setVisibleCount] = useState(5);

  useEffect(() => { setVisibleCount(5); }, [isCompareMode, brands.length]);

  const sorted = useMemo(() => [...brands].sort((a, b) => {
    return computeAvg(segmentsMap?.[b.name]) - computeAvg(segmentsMap?.[a.name]);
  }), [brands, segmentsMap]);

  const visibleBrands = sorted.slice(0, visibleCount);
  const hasMore = sorted.length > visibleCount;

  return (
    <div className={classes.scoreCard}>
      <div className={classes.cardHeader}>
        <div className={classes.cardTitleGroup}>
          <span className={classes.cardTitle}>{title}</span>
        </div>
        <div className={classes.cardHeaderRight}>
          {isCompareMode && <span className={classes.avgBadge}>Avg</span>}
          <Tooltip arrow placement="top"
            title={<span style={{ fontSize: "0.7rem" }}>{meta.desc || `Score range: 0–${maxScoreLabel}`}</span>}
            componentsProps={{ tooltip: { sx: { ...TOOLTIP_SX, padding: "6px 10px" } } }}
          >
            <span className={classes.maxScorePill}>/{maxScoreLabel}</span>
          </Tooltip>
        </div>
      </div>

      {sorted.length === 0 ? (
        <div className={classes.emptyState}>No data</div>
      ) : (
        <>
          {visibleBrands.map(brand => (
            <BrandRow key={brand.name} brand={brand} segments={segmentsMap?.[brand.name] ?? null}
              maxScore={maxScore} isCompareMode={isCompareMode} compareMode={compareMode} color={metricColor}
              showDecimal={showDecimal}
              onInfoClick={() => onInfoClick(brand)} onSegmentClick={seg => onSegmentClick(brand, seg)} />
          ))}
          {hasMore && (
            <button className={classes.loadMoreBtn} onClick={() => setVisibleCount(c => c + 5)}>
              Load more ({sorted.length - visibleCount} remaining)
            </button>
          )}
        </>
      )}
    </div>
  );
}

// ─── Simple Table Modal (Brand Pillars / Purchase Criteria) ──────────────────

function SimpleTableModal({ open, onClose, config, category, discoveryCtx }) {
  const [activeTab, setActiveTab] = useState(0);
  useEffect(() => { setActiveTab(0); }, [config]);
  if (!open || !config) return null;

  const { brand, tableType, tabs } = config;
  const tab = tabs[activeTab] || tabs[0];
  const isProduct = discoveryCtx.type === "product";

  const coreAttrs = discoveryCtx.core_attributes?.[tab.llm]?.[category]?.[tab.market]?.[tab.persona];
  const catDims   = discoveryCtx.category_dimensions?.[tab.llm]?.[category]?.[tab.market]?.[tab.persona];
  const taxData   = discoveryCtx.taxonomies?.[tab.llm]?.[category]?.[tab.market]?.[tab.persona];

  const title = tableType === "pillars"
    ? (isProduct ? ENTITY_LABELS.productDimension : ENTITY_LABELS.brandPillar)
    : ENTITY_LABELS.purchaseCriteria;

  return (
    <Modal width={960} open={open} handleClose={onClose}>
      {tabs.length > 1 && (
        <Tabs value={activeTab} onChange={(_, i) => setActiveTab(i)} variant="scrollable" scrollButtons="auto"
          sx={{
            mb: 2, borderBottom: "2px solid #e8edf7", minHeight: 42,
            "& .MuiTab-root": { textTransform: "none", fontWeight: 600, fontSize: "0.84rem", minHeight: 42, padding: "6px 22px", color: "#6b7a9d" },
            "& .Mui-selected": { color: "#4361ee" },
            "& .MuiTabs-indicator": { backgroundColor: "#4361ee", height: 3, borderRadius: "3px 3px 0 0" },
          }}
        >
          {tabs.map((t, i) => <Tab key={i} label={t.label} />)}
        </Tabs>
      )}
      <h5 style={{ marginBottom: 12, fontWeight: 700, color: "#1a1a2e" }}>{title}</h5>
      <hr style={{ marginBottom: 12 }} />
      {tableType === "pillars" ? (
        isProduct
          ? <ProductDimensionsTable brandProduct={brand.name} taxonomiesData={taxData} />
          : <BrandPillarsTable brandProduct={brand.name} coreAttributesData={coreAttrs} />
      ) : (
        <PurchaseCriteriaTable brandProduct={brand.name} categoryDimensionsData={catDims} />
      )}
    </Modal>
  );
}

// ─── Category-Analysis Tabbed Breakdown Modal ─────────────────────────────────

function CategoryBreakupModal({ open, onClose, config, category, discoveryCtx }) {
  const [activeTab, setActiveTab] = useState(0);
  useEffect(() => { setActiveTab(0); }, [config]);
  if (!open || !config) return null;

  const { brand, sections, tabs } = config;
  const tab = tabs[activeTab] || tabs[0];
  const selectedInput = { llm: tab.llm, market: tab.market, category, brand: discoveryCtx.entity, product: null, persona: tab.persona };
  const compIndexScoreMetaData = discoveryCtx.brand_scores?.[tab.llm]?.[category]?.[tab.market]?.[tab.persona] ?? [];

  return (
    <Modal width={1200} open={open} handleClose={onClose}>
      {tabs.length > 1 && (
        <Tabs value={activeTab} onChange={(_, i) => setActiveTab(i)} variant="scrollable" scrollButtons="auto"
          sx={{
            mb: 2, borderBottom: "2px solid #e8edf7", minHeight: 42,
            "& .MuiTab-root": { textTransform: "none", fontWeight: 600, fontSize: "0.84rem", minHeight: 42, padding: "6px 22px", color: "#6b7a9d" },
            "& .Mui-selected": { color: "#4361ee" },
            "& .MuiTabs-indicator": { backgroundColor: "#4361ee", height: 3, borderRadius: "3px 3px 0 0" },
          }}
        >
          {tabs.map((t, i) => <Tab key={i} label={t.label} />)}
        </Tabs>
      )}
      <BrandAuditIndexBreakups key={`${brand.name}-${activeTab}`} data={tab.extras} isOpen={open}
        type={discoveryCtx.type || "brand"} llm={tab.llm} category={category} market={tab.market} persona={tab.persona}
        selectedInput={selectedInput} brandProduct={brand.name} ctx={discoveryCtx}
        compIndexScoreMetaData={compIndexScoreMetaData}
        visibleSections={sections} isCategoryAnalysis />
    </Modal>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function CategoryScoreCardsNew({ category }) {
  const discoveryCtx = useContext(CategoryAnalysisContext);
  const selectedMetrics = discoveryCtx.state?.selectedMetrics?.length > 0
    ? discoveryCtx.state.selectedMetrics
    : ALL_METRIC_VALUES;
  const allRbiMetricsSelected = RBI_REQUIRED_METRICS.every(m => selectedMetrics.includes(m));
  const ctxLLM    = discoveryCtx.state.selectedLLM;
  const ctxMarket = discoveryCtx.state.selectedMarket;
  const ctxPersona = discoveryCtx.state.selectedPersona;

  const allLLMs    = discoveryCtx.llm || [];
  const allMarkets = discoveryCtx.markets || [];
  const allPersonas = useMemo(() =>
    (discoveryCtx?.persona || [])
      .map(p => typeof p === "object" && p?.title ? p.title : typeof p === "string" ? p : null)
      .filter(Boolean),
    [discoveryCtx?.persona]
  );

  const llmOptions     = allLLMs.map(l => ({ name: getLabel(l), value: l }));
  const marketOptions  = allMarkets.map(m => ({ name: m, value: m }));
  const personaOptions = allPersonas.map(p => ({ name: p, value: p }));

  const compareMode   = discoveryCtx.compareMode || "default";
  const isCompareMode  = compareMode === "llm";

  const multiLLMs     = isCompareMode ? allLLMs : [];
  const multiPersonas = allPersonas;
  const multiMarkets  = allMarkets;

  const compareEntities = isCompareMode ? multiLLMs : [];

  const entityColorMap = useMemo(() => {
    const map = {};
    compareEntities.forEach((e, i) => { map[e] = COMPARE_PALETTE[i % COMPARE_PALETTE.length]; });
    return map;
  }, [compareEntities.join(",")]);

  // ── Brand list ────────────────────────────────────────────────────────────────
  const competitorList = discoveryCtx.competitors?.[category] || [];
  const brands = useMemo(() => competitorList.map((comp, i) => ({
    name: comp.name, domain: extractDomain(comp.domain), idx: i,
  })), [competitorList.map(c => c.name).join(",")]);

  const brandColorMap = useMemo(() => {
    const map = {};
    brands.forEach((b, i) => { map[b.name] = BRAND_PALETTE[i % BRAND_PALETTE.length]; });
    return map;
  }, [brands.map(b => b.name).join(",")]);

  const isProduct = discoveryCtx.type === "product";

  // ── Build standard segments (rank / favorability) ─────────────────────────────
  function buildSegmentsMap(scoreType) {
    const bs = discoveryCtx.brand_scores;
    const metricColor = SCORE_TYPE_COLOR[scoreType] || BRAND_PALETTE[0];
    const result = {};
    brands.forEach(brand => {
      if (compareMode === "default") {
        const item = getBrandDataAt(bs, ctxLLM, category, ctxMarket, ctxPersona, brand.idx);
        result[brand.name] = [{ score: item?.[scoreType] ?? null, color: metricColor, entity: ctxLLM, entityLabel: getLabel(ctxLLM), extras: item?.extras ?? null }];
      } else if (compareMode === "llm") {
        result[brand.name] = multiLLMs.map(llm => { const item = getBrandDataAt(bs, llm, category, ctxMarket, ctxPersona, brand.idx); return { score: item?.[scoreType] ?? null, color: metricColor, entity: llm, entityLabel: getLabel(llm), extras: item?.extras ?? null }; });
      } else if (compareMode === "persona") {
        result[brand.name] = multiPersonas.map(persona => { const item = getBrandDataAt(bs, ctxLLM, category, ctxMarket, persona, brand.idx); return { score: item?.[scoreType] ?? null, color: metricColor, entity: persona, entityLabel: persona, extras: item?.extras ?? null }; });
      } else if (compareMode === "market") {
        result[brand.name] = multiMarkets.map(market => { const item = getBrandDataAt(bs, ctxLLM, category, market, ctxPersona, brand.idx); return { score: item?.[scoreType] ?? null, color: metricColor, entity: market, entityLabel: market, extras: item?.extras ?? null }; });
      }
    });
    return result;
  }

  // ── Build brand pillars segments ──────────────────────────────────────────────
  function buildPillarsSegmentsMap() {
    const result = {};
    brands.forEach(brand => {
      function getAvg(llm, market, persona) {
        const coreAttrs = discoveryCtx.core_attributes?.[llm]?.[category]?.[market]?.[persona];
        const taxData   = discoveryCtx.taxonomies?.[llm]?.[category]?.[market]?.[persona];
        return isProduct
          ? getProductDimensionsAvg(brand.name, taxData)
          : getBrandPillarsAvg(brand.name, coreAttrs);
      }
      if (compareMode === "default") {
        const avg = getAvg(ctxLLM, ctxMarket, ctxPersona);
        const item = getBrandDataAt(discoveryCtx.brand_scores, ctxLLM, category, ctxMarket, ctxPersona, brand.idx);
        result[brand.name] = [{ score: avg != null ? +Number(avg).toFixed(2) : null, color: RBI_COLORS.brandPillars, entity: ctxLLM, entityLabel: getLabel(ctxLLM), extras: item?.extras ?? null }];
      } else if (compareMode === "llm") {
        result[brand.name] = multiLLMs.map(llm => {
          const avg = getAvg(llm, ctxMarket, ctxPersona);
          const item = getBrandDataAt(discoveryCtx.brand_scores, llm, category, ctxMarket, ctxPersona, brand.idx);
          return { score: avg != null ? +Number(avg).toFixed(2) : null, color: RBI_COLORS.brandPillars, entity: llm, entityLabel: getLabel(llm), extras: item?.extras ?? null };
        });
      } else if (compareMode === "persona") {
        result[brand.name] = multiPersonas.map(persona => {
          const avg = getAvg(ctxLLM, ctxMarket, persona);
          const item = getBrandDataAt(discoveryCtx.brand_scores, ctxLLM, category, ctxMarket, persona, brand.idx);
          return { score: avg != null ? +Number(avg).toFixed(2) : null, color: RBI_COLORS.brandPillars, entity: persona, entityLabel: persona, extras: item?.extras ?? null };
        });
      } else if (compareMode === "market") {
        result[brand.name] = multiMarkets.map(market => {
          const avg = getAvg(ctxLLM, market, ctxPersona);
          const item = getBrandDataAt(discoveryCtx.brand_scores, ctxLLM, category, market, ctxPersona, brand.idx);
          return { score: avg != null ? +Number(avg).toFixed(2) : null, color: RBI_COLORS.brandPillars, entity: market, entityLabel: market, extras: item?.extras ?? null };
        });
      }
    });
    return result;
  }

  // ── Build purchase criteria segments ──────────────────────────────────────────
  function buildPurchaseSegmentsMap() {
    const result = {};
    brands.forEach(brand => {
      function getAvg(llm, market, persona) {
        const catDims = discoveryCtx.category_dimensions?.[llm]?.[category]?.[market]?.[persona];
        return getPurchaseCriteriaAvg(brand.name, catDims);
      }
      if (compareMode === "default") {
        const avg = getAvg(ctxLLM, ctxMarket, ctxPersona);
        const item = getBrandDataAt(discoveryCtx.brand_scores, ctxLLM, category, ctxMarket, ctxPersona, brand.idx);
        result[brand.name] = [{ score: avg != null ? +Number(avg).toFixed(2) : null, color: RBI_COLORS.purchaseCriteria, entity: ctxLLM, entityLabel: getLabel(ctxLLM), extras: item?.extras ?? null }];
      } else if (compareMode === "llm") {
        result[brand.name] = multiLLMs.map(llm => {
          const avg = getAvg(llm, ctxMarket, ctxPersona);
          const item = getBrandDataAt(discoveryCtx.brand_scores, llm, category, ctxMarket, ctxPersona, brand.idx);
          return { score: avg != null ? +Number(avg).toFixed(2) : null, color: RBI_COLORS.purchaseCriteria, entity: llm, entityLabel: getLabel(llm), extras: item?.extras ?? null };
        });
      } else if (compareMode === "persona") {
        result[brand.name] = multiPersonas.map(persona => {
          const avg = getAvg(ctxLLM, ctxMarket, persona);
          const item = getBrandDataAt(discoveryCtx.brand_scores, ctxLLM, category, ctxMarket, persona, brand.idx);
          return { score: avg != null ? +Number(avg).toFixed(2) : null, color: RBI_COLORS.purchaseCriteria, entity: persona, entityLabel: persona, extras: item?.extras ?? null };
        });
      } else if (compareMode === "market") {
        result[brand.name] = multiMarkets.map(market => {
          const avg = getAvg(ctxLLM, market, ctxPersona);
          const item = getBrandDataAt(discoveryCtx.brand_scores, ctxLLM, category, market, ctxPersona, brand.idx);
          return { score: avg != null ? +Number(avg).toFixed(2) : null, color: RBI_COLORS.purchaseCriteria, entity: market, entityLabel: market, extras: item?.extras ?? null };
        });
      }
    });
    return result;
  }

  // ── Build RBI contribution map ────────────────────────────────────────────────
  function buildRBIContributionMap() {
    const bs = discoveryCtx.brand_scores;
    const result = {};
    brands.forEach(brand => {
      function getEntry(llm, market, persona, color, entity, entityLabel) {
        const item = getBrandDataAt(bs, llm, category, market, persona, brand.idx);
        const coreAttrs = discoveryCtx.core_attributes?.[llm]?.[category]?.[market]?.[persona];
        const taxData   = discoveryCtx.taxonomies?.[llm]?.[category]?.[market]?.[persona];
        const catDims   = discoveryCtx.category_dimensions?.[llm]?.[category]?.[market]?.[persona];
        const brandPillarsAvg = isProduct
          ? getProductDimensionsAvg(brand.name, taxData)
          : getBrandPillarsAvg(brand.name, coreAttrs);
        const purchaseCriteriaAvg = getPurchaseCriteriaAvg(brand.name, catDims);
        const { total, visNorm, sentNorm, pillarsNorm, purchaseNorm, contributions } = calcBrandIndexScore({
          rank: item?.rank, favorability: item?.favorability, brandPillarsAvg, purchaseCriteriaAvg,
        });
        return { rank: item?.rank, favorability: item?.favorability, brandPillarsAvg, purchaseCriteriaAvg,
                 total, visNorm, sentNorm, pillarsNorm, purchaseNorm, contributions,
                 isProduct, color, entity, entityLabel, extras: item?.extras ?? null };
      }
      if (compareMode === "default") {
        result[brand.name] = [getEntry(ctxLLM, ctxMarket, ctxPersona, brandColorMap[brand.name] || BRAND_PALETTE[0], ctxLLM, getLabel(ctxLLM))];
      } else if (compareMode === "llm") {
        result[brand.name] = multiLLMs.map((llm, ci) => getEntry(llm, ctxMarket, ctxPersona, COMPARE_PALETTE[ci % COMPARE_PALETTE.length], llm, getLabel(llm)));
      } else if (compareMode === "persona") {
        result[brand.name] = multiPersonas.map((persona, ci) => getEntry(ctxLLM, ctxMarket, persona, COMPARE_PALETTE[ci % COMPARE_PALETTE.length], persona, persona));
      } else if (compareMode === "market") {
        result[brand.name] = multiMarkets.map((market, ci) => getEntry(ctxLLM, market, ctxPersona, COMPARE_PALETTE[ci % COMPARE_PALETTE.length], market, market));
      }
    });
    return result;
  }

  const deps = [compareMode, brands, discoveryCtx.brand_scores, discoveryCtx.core_attributes, discoveryCtx.category_dimensions, discoveryCtx.taxonomies, multiLLMs, multiPersonas, multiMarkets, ctxLLM, ctxMarket, ctxPersona];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const rbiContributionMap = useMemo(() => buildRBIContributionMap(), deps);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const rankSegs           = useMemo(() => buildSegmentsMap("rank"),        deps);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const sentimentSegs      = useMemo(() => buildSegmentsMap("favorability"), deps);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const pillarsSegs        = useMemo(() => buildPillarsSegmentsMap(),        deps);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const purchaseSegs       = useMemo(() => buildPurchaseSegmentsMap(),       deps);

  // ── Modal ─────────────────────────────────────────────────────────────────────
  const [modalOpen, setModalOpen] = useState(false);
  const [modalConfig, setModalConfig] = useState(null);
  const [simpleModalOpen, setSimpleModalOpen] = useState(false);
  const [simpleModalConfig, setSimpleModalConfig] = useState(null);

  // Rank/reach data (productRankingsArray, productMappingAttribute, baseResponses, rankMatchesIndices)
  // is stored category-wide under discoveryCtx.rank_scores, NOT per-brand under discoveryCtx.brand_scores[...].extras.
  // The Rank section of BrandAuditIndexBreakups reads those fields off `data`, so they must be merged in here —
  // otherwise the Rank "Details" modal renders blank (this is independent of compare mode).
  function getMergedExtras(brandExtras, llm, market, persona) {
    const rankExtras = discoveryCtx.rank_scores?.[llm]?.[category]?.[market]?.[persona]?.extras ?? null;
    if (!brandExtras && !rankExtras) return null;
    return { ...(brandExtras ?? {}), ...(rankExtras ?? {}) };
  }

  function openInfoModal(brand, sections) {
    const bs = discoveryCtx.brand_scores;
    let tabs;
    if (compareMode === "default") {
      const item = getBrandDataAt(bs, ctxLLM, category, ctxMarket, ctxPersona, brand.idx);
      tabs = [{ label: getLabel(ctxLLM) || "Default", llm: ctxLLM, market: ctxMarket, persona: ctxPersona, extras: getMergedExtras(item?.extras, ctxLLM, ctxMarket, ctxPersona) }];
    } else if (compareMode === "llm") {
      tabs = multiLLMs.map(llm => { const item = getBrandDataAt(bs, llm, category, ctxMarket, ctxPersona, brand.idx); return { label: getLabel(llm), llm, market: ctxMarket, persona: ctxPersona, extras: getMergedExtras(item?.extras, llm, ctxMarket, ctxPersona) }; });
    } else if (compareMode === "market") {
      tabs = multiMarkets.map(market => { const item = getBrandDataAt(bs, ctxLLM, category, market, ctxPersona, brand.idx); return { label: market, llm: ctxLLM, market, persona: ctxPersona, extras: getMergedExtras(item?.extras, ctxLLM, market, ctxPersona) }; });
    } else if (compareMode === "persona") {
      tabs = multiPersonas.map(persona => { const item = getBrandDataAt(bs, ctxLLM, category, ctxMarket, persona, brand.idx); return { label: persona, llm: ctxLLM, market: ctxMarket, persona, extras: getMergedExtras(item?.extras, ctxLLM, ctxMarket, persona) }; });
    }
    setModalConfig({ brand, sections, tabs });
    setModalOpen(true);
  }

  function openSegmentModal(brand, seg, sections) {
    const llm    = compareMode === "llm"     ? seg.entity : ctxLLM;
    const market = compareMode === "market"  ? seg.entity : ctxMarket;
    const persona = compareMode === "persona" ? seg.entity : ctxPersona;
    setModalConfig({ brand, sections, tabs: [{ label: seg.entityLabel || seg.entity || "Detail", llm, market, persona, extras: getMergedExtras(seg.extras, llm, market, persona) }] });
    setModalOpen(true);
  }

  function openSimpleModal(brand, tableType, seg = null) {
    const bs = discoveryCtx.brand_scores;
    let tabs;
    if (seg) {
      // Specific entity row clicked — show only that entity's tab
      const llm    = compareMode === "llm"     ? seg.entity : ctxLLM;
      const market = compareMode === "market"  ? seg.entity : ctxMarket;
      const persona = compareMode === "persona" ? seg.entity : ctxPersona;
      const item = getBrandDataAt(bs, llm, category, market, persona, brand.idx);
      tabs = [{ label: seg.entityLabel || seg.entity || "Detail", llm, market, persona, extras: item?.extras ?? null }];
    } else if (compareMode === "default") {
      const item = getBrandDataAt(bs, ctxLLM, category, ctxMarket, ctxPersona, brand.idx);
      tabs = [{ label: getLabel(ctxLLM) || "Default", llm: ctxLLM, market: ctxMarket, persona: ctxPersona, extras: item?.extras ?? null }];
    } else if (compareMode === "llm") {
      tabs = multiLLMs.map(llm => { const item = getBrandDataAt(bs, llm, category, ctxMarket, ctxPersona, brand.idx); return { label: getLabel(llm), llm, market: ctxMarket, persona: ctxPersona, extras: item?.extras ?? null }; });
    } else if (compareMode === "market") {
      tabs = multiMarkets.map(market => { const item = getBrandDataAt(bs, ctxLLM, category, market, ctxPersona, brand.idx); return { label: market, llm: ctxLLM, market, persona: ctxPersona, extras: item?.extras ?? null }; });
    } else if (compareMode === "persona") {
      tabs = multiPersonas.map(persona => { const item = getBrandDataAt(bs, ctxLLM, category, ctxMarket, persona, brand.idx); return { label: persona, llm: ctxLLM, market: ctxMarket, persona, extras: item?.extras ?? null }; });
    }
    setSimpleModalConfig({ brand, tableType, tabs });
    setSimpleModalOpen(true);
  }

  const pillarsLabel = isProduct ? ENTITY_LABELS.productDimension : ENTITY_LABELS.brandPillar;

  return (
    <>
      <div className={classes.sectionWrapper}>

        <h5 className={classes.panelSectionHeading}>Category Analysis</h5>

        {isCompareMode && compareEntities.length > 0 && (
          <div className={classes.analysisPanel}>
            <div className={classes.analysisPanelInfo}>
              <div className={classes.infoLegend}>
                <span className={classes.infoLegendLabel}>Comparing across {compareEntities.length} LLMs</span>
                {compareEntities.map(entity => (
                  <span key={entity} className={classes.infoLegendItem}>
                    <span className={classes.infoLegendDot} style={{ background: entityColorMap[entity] }} />
                    <span className={classes.infoLegendName}>{getLabel(entity)}</span>
                  </span>
                ))}
              </div>
              <div className={classes.infoHint}>
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none" style={{ flexShrink: 0 }}>
                  <path d="M2 3.5l3 3 3-3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                Click any row to expand breakdown
              </div>
            </div>
          </div>
        )}

        {/* ── Row 1: Full-width RBI (toggle via RBI_ROW_VIEW) — only when all RBI metrics were run ── */}
        {allRbiMetricsSelected && (
          <div className={classes.rbiRow}>
            {RBI_ROW_VIEW === "cards" ? (
              <RBIMetricCardsView
                brands={brands}
                rbiContributionMap={rbiContributionMap}
                isCompareMode={isCompareMode}
                onInfoClick={brand => openInfoModal(brand, null)}
              />
            ) : (
              <RBIStackedScoreCard
                brands={brands}
                rbiContributionMap={rbiContributionMap}
                isCompareMode={isCompareMode}
                compareMode={compareMode}
                onInfoClick={brand => openInfoModal(brand, null)}
                onSegmentClick={(brand, e) => openSegmentModal(brand, { ...e, score: e.total, entityLabel: e.entityLabel, entity: e.entity, extras: e.extras }, null)}
              />
            )}
          </div>
        )}

        {/* ── Rows 2 & 3: per-metric score cards — only selected metrics, full width when a subset, 50/50 when all four are selected ── */}
        {(() => {
          const scoreCardDefs = [
            {
              metricKey: "rank",
              node: (
                <ScoreCard title="Rank" scoreType="rank" maxScoreLabel="50"
                  brands={brands} segmentsMap={rankSegs} isCompareMode={isCompareMode} compareMode={compareMode}
                  onInfoClick={brand => openInfoModal(brand, ["rank"])}
                  onSegmentClick={(brand, seg) => openSegmentModal(brand, seg, ["rank"])}
                />
              ),
            },
            {
              metricKey: "sentiment",
              node: (
                <ScoreCard title="Sentiment" scoreType="favorability" maxScoreLabel="50"
                  brands={brands} segmentsMap={sentimentSegs} isCompareMode={isCompareMode} compareMode={compareMode}
                  onInfoClick={brand => openInfoModal(brand, ["sentiment", "sentimentDrivers"])}
                  onSegmentClick={(brand, seg) => openSegmentModal(brand, seg, ["sentiment", "sentimentDrivers"])}
                />
              ),
            },
            {
              metricKey: "coreAttribute",
              node: (
                <ScoreCard title={pillarsLabel} scoreType="brandPillars" maxScoreLabel="10"
                  brands={brands} segmentsMap={pillarsSegs} isCompareMode={isCompareMode} compareMode={compareMode}
                  onInfoClick={brand => openSimpleModal(brand, "pillars")}
                  onSegmentClick={(brand, seg) => openSimpleModal(brand, "pillars", seg)}
                />
              ),
            },
            {
              metricKey: "categoryDimension",
              node: (
                <ScoreCard title={ENTITY_LABELS.purchaseCriteria} scoreType="purchaseCriteria" maxScoreLabel="10"
                  brands={brands} segmentsMap={purchaseSegs} isCompareMode={isCompareMode} compareMode={compareMode}
                  onInfoClick={brand => openSimpleModal(brand, "purchase")}
                  onSegmentClick={(brand, seg) => openSimpleModal(brand, "purchase", seg)}
                />
              ),
            },
          ];

          const visibleCards = scoreCardDefs.filter(c => selectedMetrics.includes(c.metricKey));
          if (!visibleCards.length) return null;

          if (allRbiMetricsSelected) {
            // Unchanged default layout: Rank+Sentiment, then Brand Pillars+Purchase Criteria, 50/50
            return (
              <>
                <div className={classes.cardsRow} style={{ marginBottom: 14 }}>
                  {visibleCards[0].node}
                  {visibleCards[1].node}
                </div>
                <div className={classes.cardsRow}>
                  {visibleCards[2].node}
                  {visibleCards[3].node}
                </div>
              </>
            );
          }

          // Subset selected: each visible card takes the full row width
          return visibleCards.map(c => (
            <div key={c.metricKey} className={classes.cardsRow} style={{ marginBottom: 14 }}>
              {c.node}
            </div>
          ));
        })()}
      </div>

      <CategoryBreakupModal open={modalOpen} onClose={() => setModalOpen(false)}
        config={modalConfig} category={category} discoveryCtx={discoveryCtx}
      />
      <SimpleTableModal open={simpleModalOpen} onClose={() => setSimpleModalOpen(false)}
        config={simpleModalConfig} category={category} discoveryCtx={discoveryCtx}
      />
    </>
  );
}
